import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NotifyService } from 'src/app/core/layouts/notifications/notify/notify.service';

export interface ImageUploadConfig {
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  aspectRatio?: number; // e.g., 1 for 1:1, 16/9 for 16:9
  allowedFormats?: string[];
  allowedExtensions?: string[];
  compressMaxWidth?: number;
  compressMaxHeight?: number;
  compressQuality?: number;
}

@Component({
  selector: 'app-abp-image-upload',
  templateUrl: './abp-image-upload.component.html',
  styleUrls: ['./abp-image-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AbpImageUploadComponent),
      multi: true
    }
  ]
})
export class AbpImageUploadComponent implements ControlValueAccessor {
  @Input() label: string = 'Upload Photo';
  @Input() config: ImageUploadConfig = {};
  @Input() showRequirements: boolean = true;
  @Input() requirements: string[] = [];
  @Input() requirementsTitle: string = 'Photo Requirements:';
  
  @Output() imageSelected = new EventEmitter<{ file: File; base64: string; preview: string }>();
  @Output() imageCleared = new EventEmitter<void>();
  @Output() validationErrors = new EventEmitter<string[]>();

  @ViewChild('fileInput') fileInputRef: ElementRef<HTMLInputElement>;
  @ViewChild('uploadContainerRef', { static: false }) uploadContainerRef: ElementRef<HTMLElement>;

  // Internal state
  imageValidationErrors: string[] = [];
  selectedImageFile: File | null = null;
  imagePreview: string | null = null;
  isImageLoading: boolean = false;
  base64: string | null = null;
  public spinnerAnimationDuration: string = '0.8s';

  // Default validation constants
  private readonly DEFAULT_MIN_WIDTH = 492;
  private readonly DEFAULT_MIN_HEIGHT = 492;
  private readonly DEFAULT_MAX_WIDTH = 4864;
  private readonly DEFAULT_MAX_HEIGHT = 4864;
  private readonly DEFAULT_ASPECT_RATIO = 1; // 1:1
  private readonly DEFAULT_ALLOWED_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'];
  private readonly DEFAULT_ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];
  private readonly DEFAULT_COMPRESS_MAX_WIDTH = 640;
  private readonly DEFAULT_COMPRESS_MAX_HEIGHT = 640;
  private readonly DEFAULT_COMPRESS_QUALITY = 0.4;

  // Computed validation constants
  private get MIN_WIDTH(): number {
    return this.config.minWidth ?? this.DEFAULT_MIN_WIDTH;
  }

  private get MIN_HEIGHT(): number {
    return this.config.minHeight ?? this.DEFAULT_MIN_HEIGHT;
  }

  private get MAX_WIDTH(): number {
    return this.config.maxWidth ?? this.DEFAULT_MAX_WIDTH;
  }

  private get MAX_HEIGHT(): number {
    return this.config.maxHeight ?? this.DEFAULT_MAX_HEIGHT;
  }

  private get ASPECT_RATIO(): number {
    return this.config.aspectRatio ?? this.DEFAULT_ASPECT_RATIO;
  }

  private get ALLOWED_FORMATS(): string[] {
    return this.config.allowedFormats ?? this.DEFAULT_ALLOWED_FORMATS;
  }

  private get ALLOWED_EXTENSIONS(): string[] {
    return this.config.allowedExtensions ?? this.DEFAULT_ALLOWED_EXTENSIONS;
  }

  private get COMPRESS_MAX_WIDTH(): number {
    return this.config.compressMaxWidth ?? this.DEFAULT_COMPRESS_MAX_WIDTH;
  }

  private get COMPRESS_MAX_HEIGHT(): number {
    return this.config.compressMaxHeight ?? this.DEFAULT_COMPRESS_MAX_HEIGHT;
  }

  private get COMPRESS_QUALITY(): number {
    return this.config.compressQuality ?? this.DEFAULT_COMPRESS_QUALITY;
  }

  // ControlValueAccessor implementation
  private onChange = (value: string) => {};
  private onTouched = () => {};

  constructor(private notifyService: NotifyService) {}

  /**
   * Get accept attribute for file input
   */
  get acceptAttribute(): string {
    const formats = this.ALLOWED_FORMATS.join(',');
    const extensions = this.ALLOWED_EXTENSIONS.join(',');
    return `${formats},${extensions}`;
  }

  /**
   * Get default requirements if not provided
   */
  get defaultRequirements(): string[] {
    if (this.requirements.length > 0) {
      return this.requirements;
    }
    return [
      'Photo should be a real photo, not a graphic containing text and other design elements',
      `Image format: ${this.ALLOWED_EXTENSIONS.map(ext => ext.toUpperCase().replace('.', '')).join(', ')}`,
      `Aspect ratio: ${this.ASPECT_RATIO === 1 ? '1:1 (square)' : this.ASPECT_RATIO}`,
      `Minimum size: ${this.MIN_WIDTH} x ${this.MIN_HEIGHT} pixels`,
      `Maximum size: ${this.MAX_WIDTH} x ${this.MAX_HEIGHT} pixels`
    ];
  }

  /**
   * Handle file input change
   */
  onInputChanged(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      this.processFile(file);
    }
  }

  /**
   * Process the selected file
   */
  private processFile(file: File): void {
    if (!file) {
      return;
    }

    // Reset validation errors
    this.imageValidationErrors = [];
    this.selectedImageFile = null;
    this.imagePreview = null;
    this.base64 = null;
    this.isImageLoading = true;

    // Validate file format
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isValidFormat = this.ALLOWED_FORMATS.includes(file.type) || 
                          this.ALLOWED_EXTENSIONS.includes(fileExtension);

    if (!isValidFormat) {
      const errorMsg = `Invalid image format. Please upload a ${this.ALLOWED_EXTENSIONS.map(ext => ext.toUpperCase().replace('.', '')).join(', ')} image.`;
      this.imageValidationErrors.push(errorMsg);
      this.notifyService.error(errorMsg);
      this.validationErrors.emit(this.imageValidationErrors);
      this.isImageLoading = false;
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();
      img.src = reader.result as string;

      img.onload = () => {
        const width = img.width;
        const height = img.height;

        // Validate dimensions
        const dimensionErrors = this.validateImageDimensions(width, height);
        if (dimensionErrors.length > 0) {
          this.imageValidationErrors = dimensionErrors;
          dimensionErrors.forEach(error => this.notifyService.error(error));
          this.validationErrors.emit(this.imageValidationErrors);
          this.isImageLoading = false;
          return;
        }

        // Validate aspect ratio
        const aspectRatio = width / height;
        const tolerance = 0.01; // Allow small tolerance for floating point errors
        if (Math.abs(aspectRatio - this.ASPECT_RATIO) > tolerance) {
          const errorMsg = `Invalid aspect ratio. Image must have a ${this.ASPECT_RATIO === 1 ? '1:1 (square)' : this.ASPECT_RATIO} aspect ratio. Current ratio: ${width}:${height}`;
          this.imageValidationErrors.push(errorMsg);
          this.notifyService.error(errorMsg);
          this.validationErrors.emit(this.imageValidationErrors);
          this.isImageLoading = false;
          return;
        }

        // All validations passed
        this.selectedImageFile = file;
        this.imagePreview = reader.result as string;

        // Determine the original image format
        let imageFormat = 'image/jpeg'; // Default fallback
        let quality = this.COMPRESS_QUALITY;
        
        if (file.type) {
          // Use the file's MIME type if available
          if (file.type === 'image/png') {
            imageFormat = 'image/png';
            quality = undefined; // PNG doesn't use quality parameter
          } else if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
            imageFormat = 'image/jpeg';
          } else if (file.type === 'image/heic' || file.type === 'image/heif') {
            // HEIC/HEIF will be converted to JPEG as canvas doesn't support them
            imageFormat = 'image/jpeg';
          } else {
            // Try to determine from file extension
            const ext = fileExtension.toLowerCase();
            if (ext === '.png') {
              imageFormat = 'image/png';
              quality = undefined;
            } else if (ext === '.jpg' || ext === '.jpeg') {
              imageFormat = 'image/jpeg';
            }
          }
        }

        // Compress and convert to base64 (preserving original format)
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        let finalWidth = width;
        let finalHeight = height;

        // Only resize if larger than max, but keep aspect ratio
        if (width > this.COMPRESS_MAX_WIDTH || height > this.COMPRESS_MAX_HEIGHT) {
          const ratio = Math.min(
            this.COMPRESS_MAX_WIDTH / width,
            this.COMPRESS_MAX_HEIGHT / height
          );
          finalWidth = width * ratio;
          finalHeight = height * ratio;
        }

        canvas.width = Math.round(finalWidth);
        canvas.height = Math.round(finalHeight);

        ctx.filter = 'none';
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Convert to base64 preserving original format
        if (imageFormat === 'image/png') {
          this.base64 = canvas.toDataURL('image/png');
        } else {
          this.base64 = canvas.toDataURL('image/jpeg', quality);
        }
        
        // Update form control value
        this.onChange(this.base64);
        this.onTouched();
        this.isImageLoading = false;
        
        // Emit event
        this.imageSelected.emit({
          file: file,
          base64: this.base64,
          preview: this.imagePreview
        });

        console.log('Image validated and compressed successfully');
        console.log('Original dimensions:', width, 'x', height);
        console.log('Final dimensions:', canvas.width, 'x', canvas.height);
      };

      img.onerror = () => {
        const errorMsg = 'Failed to load image. Please try a different file.';
        this.imageValidationErrors.push(errorMsg);
        this.notifyService.error(errorMsg);
        this.validationErrors.emit(this.imageValidationErrors);
        this.isImageLoading = false;
      };
    };

    reader.onerror = () => {
      const errorMsg = 'Failed to read file. Please try again.';
      this.imageValidationErrors.push(errorMsg);
      this.notifyService.error(errorMsg);
      this.validationErrors.emit(this.imageValidationErrors);
      this.isImageLoading = false;
    };

    reader.readAsDataURL(file);
  }

  /**
   * Validate image dimensions
   */
  private validateImageDimensions(width: number, height: number): string[] {
    const errors: string[] = [];

    if (width < this.MIN_WIDTH || height < this.MIN_HEIGHT) {
      errors.push(
        `Image is too small. Minimum size is ${this.MIN_WIDTH} x ${this.MIN_HEIGHT} pixels. ` +
        `Current size: ${width} x ${height} pixels.`
      );
    }

    if (width > this.MAX_WIDTH || height > this.MAX_HEIGHT) {
      errors.push(
        `Image is too large. Maximum size is ${this.MAX_WIDTH} x ${this.MAX_HEIGHT} pixels. ` +
        `Current size: ${width} x ${height} pixels.`
      );
    }

    return errors;
  }

  /**
   * Clear the selected image and reset the form
   */
  clearImage(): void {
    this.selectedImageFile = null;
    this.imagePreview = null;
    this.base64 = null;
    this.imageValidationErrors = [];
    
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }

    // Update form control
    this.onChange('');
    this.onTouched();
    this.imageCleared.emit();
  }

  /**
   * Trigger file input click
   */
  chooseFile(): void {
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.click();
    }
  }

  /**
   * Handle drag over event
   */
  onDragOver(event: DragEvent): void {
    if (this.uploadContainerRef?.nativeElement) {
      this.uploadContainerRef.nativeElement.classList.add('dragged-over');
      this.uploadContainerRef.nativeElement.classList.remove('dragged-leave');
    }
    event.preventDefault();
  }

  /**
   * Handle drag leave event
   */
  onDragLeave(event: DragEvent): void {
    if (this.uploadContainerRef?.nativeElement) {
      this.uploadContainerRef.nativeElement.classList.add('dragged-leave');
      this.uploadContainerRef.nativeElement.classList.remove('dragged-over');
    }
    event.preventDefault();
  }

  /**
   * Handle drop event
   */
  onDropSuccess(event: DragEvent): void {
    event.preventDefault();
    
    if (this.uploadContainerRef?.nativeElement) {
      this.uploadContainerRef.nativeElement.classList.remove('dragged-over');
      this.uploadContainerRef.nativeElement.classList.remove('dragged-leave');
    }

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      this.processFile(file);
    }
  }

  // ControlValueAccessor methods
  writeValue(value: string): void {
    if (value && value !== this.base64 && value !== this.imagePreview) {
      // Handle both base64 strings and image URLs
      if (value.startsWith('data:image') || value.startsWith('data:image/')) {
        // Base64 string
        this.base64 = value;
        this.imagePreview = value;
      } else if (value.startsWith('http://') || value.startsWith('https://')) {
        // Image URL - set as preview but don't set base64
        this.imagePreview = value;
        // Note: base64 remains null for URLs, which is fine for edit mode
      }
    } else if (!value) {
      this.clearImage();
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Implement if needed
  }
}
